"use client";

/**
 * A photo added to the event's album from the trail: Tirar foto opens the
 * phone's camera straight away (an input with `capture`, one photo a press),
 * Escolher the photos already on it, several at once. A desktop has no camera
 * to open that way and gets the one button.
 *
 * Sent one at a time through the album's own route (sendEventPhoto), each shown
 * as a small picture from the moment it is chosen, with how it is going over it
 * and a tick once it is in the album. One that fails -- a trail is where the
 * signal goes -- stays with a way to send it again, for as long as the sheet is
 * open.
 */
import { useEffect, useRef, useState } from "react";
import { Camera, Check, ImagePlus, RotateCw } from "lucide-react";
import { track } from "@/lib/analytics";
import { isTouchDevice } from "@/lib/device";
import { buttonClass } from "@/components/ui/Button";
import { sendEventPhoto } from "@/components/inscricao/PhotoUploader";

const ACCEPT = "image/png, image/jpg, image/jpeg";

// url is the photo as the phone has it, to show before and after it is sent
type Item = { id: number; url: string; status: "waiting" | "sending" | "done" | "error"; percent: number };

const statusText = (item: Item) => {
  if (item.status === "waiting") return "Na fila";
  return item.percent >= 100 ? "A guardar…" : `${item.percent}%`;
};

export function LivePhotos({ eventId }: { eventId: number | string }) {
  const [items, setItems] = useState<Item[]>([]);
  const camera = useRef<HTMLInputElement>(null);
  const picker = useRef<HTMLInputElement>(null);
  const files = useRef<Map<number, File>>(new Map());
  const queue = useRef<number[]>([]);
  const counter = useRef(0);
  const busy = useRef(false);
  const urls = useRef<string[]>([]);

  // the pictures shown are held by the page until it lets them go
  useEffect(() => {
    const held = urls.current;
    return () => held.forEach((url) => URL.revokeObjectURL(url));
  }, []);
  const touch = isTouchDevice();

  const patch = (id: number, change: Partial<Item>) => setItems((list) => list.map((item) => (item.id === id ? { ...item, ...change } : item)));

  const run = async () => {
    if (busy.current) return;
    busy.current = true;
    for (let id = queue.current.shift(); id !== undefined; id = queue.current.shift()) {
      const file = files.current.get(id);
      if (!file) continue;
      const sending = id;
      patch(sending, { status: "sending", percent: 0 });
      const ok = await sendEventPhoto(eventId, file, (percent) => patch(sending, { percent }));
      patch(sending, { status: ok ? "done" : "error", percent: 100 });
      if (ok) {
        files.current.delete(sending);
      }
    }
    busy.current = false;
  };

  const add = (chosen: FileList | null, from: "camera" | "picker") => {
    if (!chosen || !chosen.length) return;
    track("live_trail_photo", { item_id: String(eventId), from, count: chosen.length });
    const added = Array.from(chosen).map((file) => {
      const id = ++counter.current;
      files.current.set(id, file);
      queue.current.push(id);
      const url = URL.createObjectURL(file);
      urls.current.push(url);
      return { id, url, status: "waiting" as const, percent: 0 };
    });
    setItems((list) => [...list, ...added]);
    run();
  };

  const retry = (id: number) => {
    patch(id, { status: "waiting", percent: 0 });
    queue.current.push(id);
    run();
  };

  return (
    <div>
      <input ref={camera} type="file" accept={ACCEPT} capture="environment" className="hidden" onChange={(e) => { add(e.target.files, "camera"); e.target.value = ""; }} />
      <input ref={picker} type="file" accept={ACCEPT} multiple className="hidden" onChange={(e) => { add(e.target.files, "picker"); e.target.value = ""; }} />
      <div className="flex gap-2">
        {touch && (
          <button type="button" onClick={() => camera.current?.click()} className={buttonClass("primary", "md", false, "min-w-0 flex-1 px-3")}>
            <Camera size={17} /> Tirar foto
          </button>
        )}
        <button type="button" onClick={() => picker.current?.click()} className={buttonClass(touch ? "outline" : "primary", "md", false, "min-w-0 flex-1 px-3")}>
          <ImagePlus size={17} /> {touch ? "Escolher" : "Adicionar fotos"}
        </button>
      </div>

      {items.length > 0 && (
        <ul className="mt-3 grid grid-cols-4 gap-2">
          {items.map((item) => (
            <li key={item.id} className="relative aspect-square overflow-hidden rounded-[10px] bg-ground">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={item.url} alt="" className={`h-full w-full object-cover ${item.status === "done" ? "" : "opacity-60"}`} />
              {item.status === "done" && (
                <span className="absolute bottom-1 right-1 flex h-5 w-5 items-center justify-center rounded-full bg-brand text-white" aria-label="No álbum">
                  <Check size={13} strokeWidth={3} />
                </span>
              )}
              {(item.status === "waiting" || item.status === "sending") && (
                <>
                  <span className="absolute inset-x-0 top-1/2 -translate-y-1/2 text-center text-[11.5px] font-bold text-ink">{statusText(item)}</span>
                  <span className="absolute inset-x-0 bottom-0 block h-1 bg-white/60">
                    <span className="block h-full bg-brand transition-[width]" style={{ width: `${item.status === "waiting" ? 0 : item.percent}%` }} />
                  </span>
                </>
              )}
              {item.status === "error" && (
                <button type="button" onClick={() => retry(item.id)} className="pressable absolute inset-0 flex flex-col items-center justify-center gap-0.5 bg-white/55 text-[11.5px] font-bold text-danger">
                  <RotateCw size={15} /> Repetir
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {items.some((item) => item.status === "done") && <p className="mt-2 text-[12.5px] text-ink-soft">Já estão no álbum do evento.</p>}
    </div>
  );
}
